import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Slide,
  Fade,
  Backdrop,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Description as DocumentIcon,
  Mic as MicIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface ChatActionMenuProps {
  show: boolean;
  onClose: () => void;
  onSelectImage: (file: File) => void;
  onSelectVideo: (file: File) => void;
  onSelectDocument: (file: File) => void;
  onStartAudioRecording: () => void;
}

interface ActionButton {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  hoverColor: string;
  accept: string;
  description: string;
  action: () => void;
}

interface UploadStatus {
  isUploading: boolean;
  progress: number;
  fileName: string;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const ChatActionMenu: React.FC<ChatActionMenuProps> = ({
  show,
  onClose,
  onSelectImage,
  onSelectVideo,
  onSelectDocument,
  onStartAudioRecording,
}) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);

  // Handle file selection
  const handleFileSelect = useCallback((file: File, type: 'image' | 'video' | 'document') => {
    setUploadStatus({
      isUploading: true,
      progress: 0,
      fileName: file.name,
      status: 'uploading',
    });

    // Simulate upload progress (replace with actual upload logic)
    const interval = setInterval(() => {
      setUploadStatus(prev => {
        if (!prev || prev.progress >= 100) {
          clearInterval(interval);
          return prev;
        }
        const newProgress = Math.min(prev.progress + Math.random() * 20, 100);
        if (newProgress >= 100) {
          setTimeout(() => {
            try {
              if (type === 'image') onSelectImage(file);
              else if (type === 'video') onSelectVideo(file);
              else onSelectDocument(file);
              
              setUploadStatus(prev => prev ? { ...prev, status: 'success' } : null);
              setTimeout(() => {
                setUploadStatus(null);
                onClose();
              }, 1500);
            } catch (error) {
              setUploadStatus(prev => prev ? { 
                ...prev, 
                status: 'error', 
                error: 'Failed to process file' 
              } : null);
            }
          }, 500);
        }
        return { ...prev, progress: newProgress };
      });
    }, 200);
  }, [onSelectImage, onSelectVideo, onSelectDocument, onClose]);

  // Handle file input change
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'document') => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file, type);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    const fileType = file.type.toLowerCase();
    
    if (fileType.startsWith('image/')) {
      handleFileSelect(file, 'image');
    } else if (fileType.startsWith('video/')) {
      handleFileSelect(file, 'video');
    } else {
      handleFileSelect(file, 'document');
    }
  }, [handleFileSelect]);

  // Action buttons configuration
  const actionButtons: ActionButton[] = [
    {
      id: 'image',
      label: 'Image',
      icon: <ImageIcon />,
      color: '#4CAF50',
      hoverColor: '#45a049',
      accept: 'image/*',
      description: 'Share photos and images',
      action: () => {
        setActiveAction('image');
        if (fileInputRef.current) {
          fileInputRef.current.accept = 'image/*';
          fileInputRef.current.onchange = (e) => handleFileChange(e as any, 'image');
          fileInputRef.current.click();
        }
      },
    },
    {
      id: 'video',
      label: 'Video',
      icon: <VideoIcon />,
      color: '#FF9800',
      hoverColor: '#f57c00',
      accept: 'video/*',
      description: 'Share video files',
      action: () => {
        setActiveAction('video');
        if (fileInputRef.current) {
          fileInputRef.current.accept = 'video/*';
          fileInputRef.current.onchange = (e) => handleFileChange(e as any, 'video');
          fileInputRef.current.click();
        }
      },
    },
    {
      id: 'document',
      label: 'Document',
      icon: <DocumentIcon />,
      color: '#2196F3',
      hoverColor: '#1976d2',
      accept: '.pdf,.doc,.docx,.txt,.rtf',
      description: 'Share PDFs and documents',
      action: () => {
        setActiveAction('document');
        if (fileInputRef.current) {
          fileInputRef.current.accept = '.pdf,.doc,.docx,.txt,.rtf';
          fileInputRef.current.onchange = (e) => handleFileChange(e as any, 'document');
          fileInputRef.current.click();
        }
      },
    },
    {
      id: 'audio',
      label: 'Voice',
      icon: <MicIcon />,
      color: '#E91E63',
      hoverColor: '#c2185b',
      accept: '',
      description: 'Record audio message',
      action: () => {
        onStartAudioRecording();
        onClose();
      },
    },
  ];

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
      />

      {/* Backdrop */}
      <Backdrop
        open={show}
        onClick={onClose}
        sx={{
          zIndex: 1200,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      />

      {/* Action Menu */}
      <Slide direction="up" in={show} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 80,
            left: 0,
            right: 0,
            margin: '0 auto',
            width: 'fit-content',
            maxWidth: 400,
            minWidth: 320,
            zIndex: 1300,
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            // Ensure it doesn't go off-screen
            '@media (max-width: 400px)': {
              minWidth: 280,
              maxWidth: '90vw',
            },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              background: dragOver 
                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(0, 119, 200, 0.05) 0%, rgba(76, 175, 80, 0.05) 100%)',
              transition: 'background 0.3s ease',
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary">
              {dragOver ? 'Drop file to share' : 'Share Content'}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Upload Status */}
          {uploadStatus && (
            <Fade in>
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {uploadStatus.status === 'uploading' && <UploadIcon color="primary" fontSize="small" />}
                  {uploadStatus.status === 'success' && <CheckIcon color="success" fontSize="small" />}
                  {uploadStatus.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                  <Typography variant="body2" fontWeight="medium">
                    {uploadStatus.status === 'uploading' && 'Uploading...'}
                    {uploadStatus.status === 'success' && 'Upload complete!'}
                    {uploadStatus.status === 'error' && 'Upload failed'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {uploadStatus.fileName}
                </Typography>
                {uploadStatus.status === 'uploading' && (
                  <LinearProgress
                    variant="determinate"
                    value={uploadStatus.progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(0, 119, 200, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: 'linear-gradient(90deg, #0077c8 0%, #4caf50 100%)',
                      },
                    }}
                  />
                )}
                {uploadStatus.status === 'error' && uploadStatus.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {uploadStatus.error}
                  </Alert>
                )}
              </Box>
            </Fade>
          )}

          {/* Action Buttons */}
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              {actionButtons.map((button) => (
                <Box
                  key={button.id}
                  onClick={button.action}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: activeAction === button.id ? `${button.color}15` : 'rgba(0, 0, 0, 0.02)',
                    border: `2px solid ${activeAction === button.id ? button.color : 'transparent'}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: activeAction === button.id ? 'translateY(-2px)' : 'translateY(0)',
                    '&:hover': {
                      backgroundColor: `${button.color}10`,
                      border: `2px solid ${button.color}40`,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 25px ${button.color}20`,
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: button.color,
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: button.hoverColor,
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {button.icon}
                  </Box>

                  {/* Label */}
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.primary"
                    sx={{ textAlign: 'center' }}
                  >
                    {button.label}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textAlign: 'center',
                      fontSize: '11px',
                      lineHeight: 1.2,
                      opacity: 0.8,
                    }}
                  >
                    {button.description}
                  </Typography>

                  {/* Hover overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${button.color}08 0%, ${button.color}03 100%)`,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      '.MuiBox-root:hover &': {
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Drag and drop hint */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: dragOver ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 119, 200, 0.05)',
                border: dragOver ? '2px dashed #4CAF50' : '2px dashed rgba(0, 119, 200, 0.2)',
                transition: 'all 0.3s ease',
              }}
            >
              <Typography
                variant="caption"
                color={dragOver ? 'success.main' : 'text.secondary'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  fontWeight: 500,
                }}
              >
                <AttachFileIcon fontSize="small" />
                {dragOver ? 'Release to upload' : 'Or drag and drop files here'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default ChatActionMenu;
